'use client';

// V1.25.1020 — NouveautesBlock : annonce les dernières nouveautés produit
// sous forme de cartes. Image optionnelle par item.
//
// Layouts disponibles :
//   - 'cards-grid'  : 3 colonnes responsive (par défaut)
//   - 'cards-stack' : 1 colonne large
//   - 'timeline'    : timeline verticale avec date à gauche
//
// Chaque item : { badge, badge_custom, date, title, description,
//                 image_url (optionnel), cta_text, cta_url }
import { ArrowRight, Sparkles, Calendar, Beaker, Rocket, Wrench } from 'lucide-react';
import { resolveMediaUrl } from '@/lib/api';
import { applyTextStyle } from '@/lib/textStyle';

const BADGE_STYLES = {
  Nouveau:   { bg: 'rgba(226,114,40,0.12)',  fg: '#c2410c', icon: Sparkles },
  'Bientôt': { bg: 'rgba(59,130,246,0.12)',  fg: '#1d4ed8', icon: Rocket },
  Beta:      { bg: 'rgba(147,51,234,0.12)',  fg: '#7e22ce', icon: Beaker },
  'Amélioré':{ bg: 'rgba(34,197,94,0.12)',   fg: '#15803d', icon: Wrench },
};

function Badge({ item }) {
  const label = item.badge === 'custom' ? (item.badge_custom || '') : (item.badge || '');
  if (!label) return null;
  const style = BADGE_STYLES[label] || { bg: '#f1f5f9', fg: '#475569', icon: Sparkles };
  const Icon = style.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px',
      background: style.bg, color: style.fg,
      fontSize: 11, fontWeight: 700,
      letterSpacing: '0.05em', textTransform: 'uppercase',
    }}>
      <Icon size={12} strokeWidth={2.5} />
      {label}
    </span>
  );
}

function NouveauteCard({ item, variant = 'grid' }) {
  const imageUrl = item.image_url ? resolveMediaUrl(item.image_url) : null;
  const hasCta = item.cta_text && item.cta_url;
  const compact = variant === 'timeline';
  return (
    <article style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s, transform 0.2s',
      height: '100%',
    }} className="nouveaute-card">
      {imageUrl && (
        <div style={{
          width: '100%',
          aspectRatio: variant === 'stack' ? '21 / 9' : '16 / 9',
          background: '#f8fafc',
          overflow: 'hidden',
        }}>
          <img
            src={imageUrl}
            alt={item.title || ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>
      )}
      <div style={{
        padding: compact ? '18px 20px' : '24px 24px 22px',
        display: 'flex', flexDirection: 'column', gap: 10, flex: 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Badge item={item} />
          {item.date && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 12, color: '#94a3b8', fontWeight: 500,
            }}>
              <Calendar size={11} /> {item.date}
            </span>
          )}
        </div>
        {item.title && (
          <h3 style={{
            margin: 0,
            fontSize: variant === 'stack' ? 22 : 18,
            fontWeight: 800,
            lineHeight: 1.2,
            color: 'var(--color-text)',
            letterSpacing: '-0.01em',
          }}>{item.title}</h3>
        )}
        {item.description && (
          <p style={{
            margin: 0,
            fontSize: 14, lineHeight: 1.55,
            color: '#64748b', flex: 1,
          }}>{item.description}</p>
        )}
        {hasCta && (
          <a
            href={item.cta_url}
            style={{
              marginTop: 6,
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 13, fontWeight: 600,
              color: 'var(--color-primary)',
              textDecoration: 'none',
              alignSelf: 'flex-start',
            }}
          >
            {item.cta_text} <ArrowRight size={14} />
          </a>
        )}
      </div>
    </article>
  );
}

export default function NouveautesBlock({ config }) {
  const items = Array.isArray(config.items) ? config.items : [];
  if (items.length === 0) return null;
  const layout = config.layout || 'cards-grid';
  const showHeader = config.title || config.subtitle;

  return (
    <section style={{ padding: '44px 0', background: 'linear-gradient(180deg, #fafafa 0%, #fff 100%)' }}>
      <div className="container-page">
        {showHeader && (
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 50px' }}>
            {config.title && (
              <h2 style={applyTextStyle(config.title_style, {
                margin: '0 0 14px',
                fontSize: 'clamp(28px, 4vw, 40px)',
                fontWeight: 800, lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: 'var(--color-text)',
              })}>{config.title}</h2>
            )}
            {config.subtitle && (
              <p style={applyTextStyle(config.subtitle_style, {
                margin: 0,
                fontSize: 17, lineHeight: 1.55,
                color: '#64748b',
              })}>{config.subtitle}</p>
            )}
          </div>
        )}

        {layout === 'cards-stack' && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 28,
            maxWidth: 880, margin: '0 auto',
          }}>
            {items.map((it, i) => <NouveauteCard key={i} item={it} variant="stack" />)}
          </div>
        )}

        {layout === 'timeline' && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 0,
            maxWidth: 880, margin: '0 auto',
            position: 'relative',
          }}>
            {items.map((it, i) => (
              <div key={i} style={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                gap: 24,
                paddingBottom: 28,
                position: 'relative',
              }} className="nouveautes-timeline-row">
                <div style={{
                  textAlign: 'right',
                  paddingTop: 18,
                  fontSize: 13, fontWeight: 700,
                  color: '#94a3b8',
                  borderRight: '2px solid #e5e7eb',
                  paddingRight: 18,
                  position: 'relative',
                }}>
                  {it.date || ''}
                  <span style={{
                    position: 'absolute',
                    right: -8, top: 22,
                    width: 14, height: 14,
                    background: 'var(--color-primary)',
                    border: '3px solid #fff',
                    boxShadow: '0 0 0 1px #e5e7eb',
                  }} />
                </div>
                <div>
                  <NouveauteCard item={it} variant="timeline" />
                </div>
              </div>
            ))}
          </div>
        )}

        {layout === 'cards-grid' && (
          <div className="nouveautes-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 24,
          }}>
            {items.map((it, i) => <NouveauteCard key={i} item={it} variant="grid" />)}
          </div>
        )}
      </div>

      {/* Hover effect via global styles (style jsx serait scopé client) */}
      <style>{`
        .nouveaute-card:hover {
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.10);
          transform: translateY(-2px);
        }
        @media (max-width: 720px) {
          .nouveautes-timeline-row {
            grid-template-columns: 60px 1fr !important;
            gap: 14px !important;
          }
        }
      `}</style>
    </section>
  );
}
